import { TextFieldProps as MuiTextFieldProps } from '@mui/material';
import { FocusEvent, useState } from 'react';
import {
    FieldPath, FieldValues, PathValue, UnpackNestedValue, UseControllerProps, useController
} from 'react-hook-form';

import { addDefaultRequiredRule } from '../utils/addDefaultRequiredRule';


export type ControlledTextFieldProps<
    TFieldValues extends FieldValues,
    TName extends FieldPath<TFieldValues>
> = {
    hideHelperTextOnBlur?: boolean;
    hideRequiredIndicator?: boolean;
    maxLength?: number;
} & UseControllerProps<TFieldValues, TName> & MuiTextFieldProps;

export function useControlledTextFieldProps<
    TFieldValues extends FieldValues,
    TName extends FieldPath<TFieldValues>
>({
    hideHelperTextOnBlur,
    hideRequiredIndicator,
    maxLength,
    name,
    ref,
    rules: rulesProp,
    ...otherProps
}: ControlledTextFieldProps<TFieldValues, TName>) {
    const [ isFocused, setIsFocused ] = useState(false);

    const {
        field: { ref: inputRef, onChange, ...otherFieldProps },
        fieldState: { error }
    } = useController({
        name,
        rules: addDefaultRequiredRule(rulesProp, otherProps.required, otherProps.label),
        defaultValue: otherProps.SelectProps?.multiple
            ? [] as UnpackNestedValue<PathValue<TFieldValues, TName>>
            : otherProps.defaultValue || '' as UnpackNestedValue<PathValue<TFieldValues, TName>>
    });

    if (otherProps.required && hideRequiredIndicator) {
        otherProps.required = false;
    }

    return {
        ...otherProps,
        ...otherFieldProps,
        inputProps: {
            maxLength,
            ...otherProps.inputProps
        },
        inputRef,
        error: !!error?.message,
        helperText: (hideHelperTextOnBlur && !isFocused) ? undefined : (error ? error.message : otherProps.helperText),
        onBlur: () => {
            setIsFocused(false);
            otherFieldProps.onBlur();
        },
        onFocus: (event: FocusEvent<HTMLTextAreaElement | HTMLInputElement, Element>) => {
            setIsFocused(true);
            otherProps.onFocus?.(event);
        },
        onChange: (event: any) => {
            otherProps.onChange?.(event);
            onChange(event);
        }
    };
}
